/**
 * AssignSectionDirectorDialog
 *
 * Modal para asignar (o transferir) la dirección de una sección concreta
 * de un club. Llama al callable `assignSectionDirector`.
 *
 * Reglas:
 *  - Sólo lista miembros activos del club, con cuenta vinculada y ≥ 16 años.
 *  - Excluye al director general (que ya tiene cobertura del club entero) y al
 *    director actual de la sección si lo hay.
 *  - Pide escribir el nombre del candidato como confirmación.
 */

import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { AlertTriangle, Loader2, Search, ShieldCheck } from "lucide-react";

import { Modal, ModalFooter, Button } from "@/components/ui";
import { functions } from "@/lib/firebase";
import { useMembersByClub } from "@/hooks/usePersons";
import { CLUB_TYPE_LABELS } from "@/types";
import type { ClubType, MemberWithPerson } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  clubName: string;
  section: ClubType;
  /** uid del director general del club (para excluirlo de la lista). */
  generalDirectorUserId?: string;
  /** personId del director ACTUAL de la sección (para excluirlo). */
  currentSectionDirectorPersonId?: string;
  onSuccess?: () => void;
}

interface AssignResponse {
  success: boolean;
  roleId: string;
  newDirectorUserId?: string;
  alreadyAssigned?: boolean;
}

const MIN_AGE = 16;

function calcAge(birthDate: unknown): number | null {
  if (!birthDate) return null;
  let date: Date | null = null;
  if (
    typeof birthDate === "object" &&
    birthDate !== null &&
    "toDate" in (birthDate as object) &&
    typeof (birthDate as { toDate?: () => Date }).toDate === "function"
  ) {
    date = (birthDate as { toDate: () => Date }).toDate();
  } else if (birthDate instanceof Date) {
    date = birthDate;
  }
  if (!date) return null;
  const ms = Date.now() - date.getTime();
  return Math.floor(ms / (365.25 * 24 * 3600 * 1000));
}

export default function AssignSectionDirectorDialog({
  isOpen,
  onClose,
  clubId,
  clubName,
  section,
  generalDirectorUserId,
  currentSectionDirectorPersonId,
  onSuccess,
}: Props) {
  const { data: members, isLoading: loading } = useMembersByClub(clubId);

  const [search, setSearch] = useState("");
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const sectionLabel = CLUB_TYPE_LABELS[section];

  const eligible = useMemo(() => {
    if (!members) return [] as MemberWithPerson[];
    return members
      .filter((m) => m.status === "active")
      .filter((m) => !!m.person?.linkedUserId)
      .filter((m) => m.person.linkedUserId !== generalDirectorUserId)
      .filter((m) => m.person.id !== currentSectionDirectorPersonId)
      .filter((m) => {
        const age = calcAge(m.person.birthDate);
        if (age == null) return true; // si no hay fecha, no bloqueamos en cliente
        return age >= MIN_AGE;
      })
      .sort((a, b) =>
        (a.person.fullName || "").localeCompare(b.person.fullName || ""),
      );
  }, [members, generalDirectorUserId, currentSectionDirectorPersonId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((m) => {
      const name = (m.person.fullName || "").toLowerCase();
      const email = (m.person.email || "").toLowerCase();
      const dni = (m.person.idDocumentNumber || "").toLowerCase();
      return name.includes(q) || email.includes(q) || dni.includes(q);
    });
  }, [eligible, search]);

  const selected = useMemo(
    () => eligible.find((m) => m.person.id === selectedPersonId) || null,
    [eligible, selectedPersonId],
  );

  const expectedConfirm = (selected?.person.fullName || "").trim();
  const confirmMatches =
    !!selected &&
    confirmName.trim().toLowerCase() === expectedConfirm.toLowerCase();

  const handleClose = () => {
    if (submitting) return;
    setSearch("");
    setSelectedPersonId(null);
    setConfirmName("");
    setError(null);
    setDone(false);
    onClose();
  };

  const handleAssign = async () => {
    if (!selected || !confirmMatches) return;
    setSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { clubId: string; section: ClubType; newPersonId: string },
        AssignResponse
      >(functions, "assignSectionDirector");
      await fn({ clubId, section, newPersonId: selected.person.id });
      setDone(true);
      onSuccess?.();
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "No se pudo asignar el director.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Director asignado"
        size="md"
      >
        <div className="space-y-4 text-center py-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            {selected?.person.fullName} es ahora director(a) de {sectionLabel}{" "}
            en {clubName}.
          </h3>
          <p className="text-sm text-gray-600">
            Verá únicamente los miembros de esta sección en su próximo ingreso.
            Si no tiene cuenta activa, pídele que inicie sesión con su correo.
          </p>
        </div>
        <ModalFooter>
          <Button onClick={handleClose} className="w-full">
            Listo
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Asignar director de ${sectionLabel}`}
      description="Elige un miembro del club con cuenta vinculada y al menos 16 años."
      size="lg"
      closeOnOverlay={!submitting}
      closeOnEscape={!submitting}
    >
      <div className="space-y-4">
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 space-y-1">
            <p className="font-medium">
              El nuevo director de sección verá y gestionará sólo los miembros
              de {sectionLabel}.
            </p>
            <p>
              Si más adelante quieres devolverle el rol de miembro regular, usa
              "Quitar director" en el panel de Dirección.
            </p>
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar miembro por nombre, correo o documento…"
            className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
            disabled={submitting}
          />
        </div>

        <div className="border border-gray-200 rounded-xl max-h-72 overflow-y-auto divide-y divide-gray-100">
          {loading && (
            <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Cargando miembros…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {eligible.length === 0
                ? "No hay miembros elegibles. El candidato debe ser miembro activo del club, tener cuenta vinculada y mínimo 16 años."
                : "Sin resultados."}
            </div>
          )}
          {!loading &&
            filtered.map((m) => {
              const isSelected = m.person.id === selectedPersonId;
              const age = calcAge(m.person.birthDate);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setSelectedPersonId(m.person.id);
                    setConfirmName("");
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-purple-50/60 transition-colors ${
                    isSelected ? "bg-purple-50" : ""
                  }`}
                  disabled={submitting}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {m.person.fullName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {m.person.email || "Sin correo"}
                        {age != null ? ` · ${age} años` : ""}
                      </p>
                    </div>
                    {isSelected && (
                      <span className="text-xs font-semibold text-purple-700">
                        Seleccionado
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
        </div>

        {selected && (
          <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4 space-y-2">
            <p className="text-sm text-purple-900">
              Para confirmar, escribe el nombre completo del nuevo director:
            </p>
            <p className="text-sm font-mono text-purple-700">
              {expectedConfirm}
            </p>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={expectedConfirm}
              className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500"
              disabled={submitting}
              autoComplete="off"
            />
          </div>
        )}

        {error && (
          <div className="text-sm rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            {error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleAssign}
          disabled={!confirmMatches || submitting}
          className="bg-gradient-to-r from-purple-500 to-purple-600"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Asignando…
            </>
          ) : (
            <>Asignar como director</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
